import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { questions, likertScale, TOTAL_QUESTIONS } from '../data/questions';
import { processQuizAnswers } from '../data/scoring';
import { validateAnswers, calculateProgress } from '../utils/quizLogic';
import '../App.css';

// Fisher-Yates shuffle algorithm
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

function QuizPage() {
  const navigate = useNavigate();
  
  // Helper function to clear saved tarot card images
  const clearSavedImages = () => {
    try {
      const keysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith('tarot_card_image_')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => sessionStorage.removeItem(key));
      if (keysToRemove.length > 0) {
        console.log('Cleared', keysToRemove.length, 'saved tarot card image(s) from sessionStorage');
      }
    } catch (error) {
      console.error('Error clearing saved images from sessionStorage:', error);
    }
  };
  
  // Create shuffled order of question indices on mount (memoized so it doesn't change)
  const shuffledOrder = useMemo(() => {
    return shuffleArray(Array.from({ length: TOTAL_QUESTIONS }, (_, i) => i));
  }, []);
  
  const [currentQuestion, setCurrentQuestion] = useState(0);
  // Answers array indexed by original question index
  const [answers, setAnswers] = useState(Array(TOTAL_QUESTIONS).fill(null));

  // Track quiz start in PostHog
  useEffect(() => {
    if (window.posthog) {
      window.posthog.capture('quiz_started');
    }
  }, []);

  const handleAnswerSelect = (value) => {
    const newAnswers = [...answers];
    // Store answer at original question index
    const originalIndex = shuffledOrder[currentQuestion];
    newAnswers[originalIndex] = value;
    setAnswers(newAnswers);
    
    // Capture current question index for use in setTimeout
    const currentQ = currentQuestion;
    
    // Auto-advance to next question after a brief delay (but not on last question)
    const isLastQuestion = currentQ === TOTAL_QUESTIONS - 1;
    if (!isLastQuestion) {
      setTimeout(() => {
        // Not the last question - go to next
        setCurrentQuestion(currentQ + 1);
      }, 300); // Small delay for visual feedback
    }
    // Last question: don't auto-advance, user must click SUBMIT
  };

  const handleNext = () => {
    if (currentQuestion < TOTAL_QUESTIONS - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Last question - check if all answered and proceed
      if (validateAnswers(answers)) {
        handleComplete();
      }
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleComplete = () => {
    if (!validateAnswers(answers)) {
      alert('Please answer all questions before completing the quiz.');
      return;
    }

    try {
      // Clear old saved images when starting a new quiz
      clearSavedImages();
      
      const results = processQuizAnswers(answers);

      // Track quiz completion in PostHog
      if (window.posthog) {
        window.posthog.capture('quiz_completed', {
          personality_type: results.profile,
          personality_name: results.personality?.name || ''
        });
      }

      // Notify Slack (fire and forget — don't block the user)
      fetch('/api/slack-notify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'quiz_completed',
          personalityType: results.profile,
          personalityName: results.personality?.name || ''
        })
      }).catch(() => {});

      // Navigate to loading2 page, passing results
      navigate('/loading2', { state: { results, answers } });
    } catch (error) {
      console.error('Error processing quiz:', error);
      alert('An error occurred. Please try again.');
    }
  };

  const progress = calculateProgress(currentQuestion);
  // Get the original question index for current displayed question
  const originalQuestionIndex = shuffledOrder[currentQuestion];
  const question = questions[originalQuestionIndex];
  // Get answer from original question index
  const currentAnswer = answers[originalQuestionIndex];

  return (
    <div className="app-container">
      {/* Blue gradient overlay */}
      <div className="blue-gradient-overlay"></div>

      <div className="main-content quiz-content">
        {/* Question Number */}
        <div className="question-number">
          <p className="question-number-text">QUESTION {currentQuestion + 1}</p>
        </div>

        {/* Progress indicator */}
        <div className="quiz-progress">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progress}%` }}></div>
          </div>
        </div>

        {/* Question */}
        <div className="question-container">
          <h2 className="question-text">{question.text}</h2>
        </div>

        {/* Likert Scale - Horizontal Radio Buttons */}
        <div className="likert-scale-horizontal">
          <div className="likert-buttons-row">
            {[1, 2, 3, 4, 5].map((value) => (
              <button
                key={value}
                className={`likert-radio ${currentAnswer && value <= currentAnswer ? 'filled' : ''}`}
                onClick={() => handleAnswerSelect(value)}
                aria-label={`${likertScale[value]}`}
              />
            ))}
          </div>
          <div className="likert-labels">
            <span className="label-disagree">DISAGREE</span>
            <span className="label-agree">AGREE</span>
          </div>
        </div>

        {/* Navigation buttons */}
        <div className="quiz-navigation">
          <button
            className="nav-button prev-button"
            onClick={handlePrevious}
            disabled={currentQuestion === 0}
          >
            BACK
          </button>
          
          {currentQuestion === TOTAL_QUESTIONS - 1 ? (
            <button
              className="nav-button complete-button"
              onClick={handleComplete}
              disabled={!validateAnswers(answers)}
            >
              SUBMIT
            </button>
          ) : (
            <button
              className="nav-button next-button"
              onClick={handleNext}
              disabled={currentAnswer === null}
            >
              NEXT
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default QuizPage;

